import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const serviceAreas = [
  {
    name: 'Leicester',
    position: [52.6369, -1.1398],
    isOffice: true,
    address: '4 Rivers Street, Leicester, LE3 5RX',
  },
  {
    name: 'Birstall',
    position: [52.6725, -1.1147],
  },
  {
    name: 'Rothley',
    position: [52.7119, -1.1351],
  },
  {
    name: 'Melton Mowbray',
    position: [52.7657, -0.8862],
  },
  {
    name: 'Loughborough',
    position: [52.7721, -1.2070],
  },
  {
    name: 'Thurmaston',
    position: [52.6762, -1.1041],
  },
  {
    name: 'Mountsorrel',
    position: [52.7036, -1.1298],
  },
];

const leicesterCenter = [52.6369, -1.1398];

const greenPinSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
  <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z" fill="FILLCOLOR"/>
  <circle cx="14" cy="13" r="6" fill="#fff"/>
</svg>`;

const defaultIcon = L.divIcon({
  className: 'service-area-pin',
  html: greenPinSvg.replace('FILLCOLOR', '#22c55e'),
  iconSize: [28, 40],
  iconAnchor: [14, 40],
  popupAnchor: [0, -36],
});

const activeIcon = L.divIcon({
  className: 'service-area-pin-active',
  html: greenPinSvg.replace('FILLCOLOR', '#ca2319'),
  iconSize: [34, 48],
  iconAnchor: [17, 48],
  popupAnchor: [0, -44],
});

const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, zoom, { duration: 1.2 });
  }, [center, zoom, map]);
  return null;
};

const ServiceAreaMarker = ({ area, selected, onClick }) => {
  const markerRef = useRef(null);

  useEffect(() => {
    if (selected && markerRef.current) {
      markerRef.current.openPopup();
    }
  }, [selected]);

  return (
    <Marker
      ref={markerRef}
      position={area.position}
      icon={selected ? activeIcon : defaultIcon}
      eventHandlers={{ click: () => onClick(area) }}
    >
      <Popup>
        <strong>{area.name}</strong><br />
        {area.isOffice ? '4 Rivers Street, Leicester, LE3 5RX' : 'Service Area'}
      </Popup>
    </Marker>
  );
};

const ServiceAreaMap = () => {
  const [selectedArea, setSelectedArea] = useState(null);
  const [mapCenter, setMapCenter] = useState(leicesterCenter);
  const [mapZoom, setMapZoom] = useState(11);

  const handleLocationClick = (area) => {
    setSelectedArea(area.name);
    setMapCenter(area.position);
    setMapZoom(13);
  };

  const handleShowAll = () => {
    setSelectedArea(null);
    setMapCenter(leicesterCenter);
    setMapZoom(11);
  };

  return (
    <section className="service-area-section">
      <div className="auto-container">
        <div className="sec-title centred">
          <h2>Our Service Areas</h2>
          <p>We provide driving lessons across Leicester and surrounding areas</p>
        </div>
        <div className="service-area-wrapper">
          <div className="service-area-sidebar">
            <button
              className={`service-area-btn ${selectedArea === null ? 'active' : ''}`}
              onClick={handleShowAll}
            >
              <i className="bi bi-geo-alt-fill"></i>
              <span>Show All Locations</span>
            </button>
            {serviceAreas.map((area) => (
              <button
                key={area.name}
                className={`service-area-btn ${selectedArea === area.name ? 'active' : ''} ${area.isOffice ? 'is-office' : ''}`}
                onClick={() => handleLocationClick(area)}
              >
                <i className={`bi ${area.isOffice ? 'bi-building' : 'bi-geo-alt-fill'}`}></i>
                <div className="area-info">
                  <span className="area-name">{area.name}</span>
                </div>
              </button>
            ))}
          </div>
          <div className="service-area-map">
            <MapContainer
              center={leicesterCenter}
              zoom={11}
              style={{ height: '450px', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {serviceAreas.map((area) => (
                <ServiceAreaMarker
                  key={area.name}
                  area={area}
                  selected={selectedArea === area.name}
                  onClick={handleLocationClick}
                />
              ))}
              <MapUpdater center={mapCenter} zoom={mapZoom} />
            </MapContainer>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaMap;
